import { css } from '@emotion/core';
import { Col, Collapse, Icon, Input, Row, Select } from 'antd';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import { formatPage, getForumFieldOptions } from '@/src/forum/helper';
import Button from '@/src/shared/components/Button';
import FilterSection, {
  FILTER_KEY,
  filterCollapseProps,
} from '@/src/shared/components/FilterSection';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import SearchTag from '@/src/shared/components/SearchTag';
import { bp, colors, form as formStyle, space } from '@/styles';
import formatURL from '@/utils/formatURL';
import { escapeGTMParam, trackGTMEvent } from '@/utils/utils';

const { m } = space;

const { Panel } = Collapse;

const divider = css`
  padding-bottom: 2rem;
  border-bottom: 1px solid ${colors.accentGrayLight};
`;

const style = {
  filterWrapper: css`
    @media ${bp.md} {
      width: 100%;
    }
    > .ant-collapse-header {
      border-bottom: 1px solid ${colors.accentGrayLight};
      margin-bottom: 20px;
    }
  `,
  filters: css`
    padding: 4px 16px 24px;
    margin-top: 24px;
    margin-bottom: 24px;
    margin-left: -16px;
    margin-right: -16px;
    box-shadow: 0 6px 12px 0 rgba(35, 32, 32, 0.1);
    background-color: ${colors.white};
    @media ${bp.md} {
      padding: 4px 32px 24px;
      border-radius: 4px;
      margin-left: initial;
      margin-right: initial;
      margin-top: 20px;
    }
    > .ant-collapse {
      > .ant-collapse-item {
        border-bottom: none;
      }
    }
    .ant-collapse {
      &-header {
        padding: 20px 0 !important;
      }
      &-content-box {
        padding: 0;
        padding-top: 0 !important;
        .ant-collapse-content-box {
          padding: 0 0 20px !important;
        }
      }
      &-arrow {
        right: 0 !important;
        left: auto !important;
      }
    }
    label[for] {
      margin-bottom: 12px;
    }
  `,
  title: css`
    line-height: 3.2rem;
  `,
  searchInput: css`
    display: flex;
    align-items: center;
    ${divider}
  `,
  buttonContainer: css`
    margin-top: 2rem;
    display: flex;
    justify-content: flex-end;
  `,
  filterTagsHidden: css`
    overflow: hidden;
    white-space: nowrap;
    display: none;
  `,
  filterTags: css`
    margin-bottom: 16px;
  `,
  collapse: css`
    background-color: transparent;
  `
};

const SearchInput = props => {
  const { loading, disabled, ..._props } = props;
  return (
    <div css={style.searchInput}>
      <Input
        prefix={<Icon type="search" />}
        placeholder="Search"
        css={formStyle.inputGray}
        {..._props}
      />
    </div>
  );
};

const strToArr = v => v.split(',');

const FIELD_NAMES = {
  TOPICS: 'Topics',
  STATES: 'States',
  SEARCHTEXT: 'SearchText',
};

const queryFields = [
  { key: 'page', option: 'Page', formatter: formatPage },
  { key: 'pageSize', option: 'PageSize' },
  { key: 'sortField', option: 'SortField' },
  { key: 'sortOrder', option: 'SortOrder' },
  { key: 'topics', option: FIELD_NAMES.TOPICS, field: true, formatter: strToArr },
  { key: 'states', option: FIELD_NAMES.STATES, field: true, formatter: strToArr },
  { key: 'searchText', option: FIELD_NAMES.SEARCHTEXT, field: true },
];

const formatTags = (tags, states, topics) => {
  return tags.map(tag => {
    if (!tag.children && tag.value) {
      if (tag.input === FIELD_NAMES.STATES) {
        const state = states.find(({ Value }) => Value === tag.value);
        return {
          ...tag,
          children: state && state.Text,
        };
      }
      const topic = topics.find(({ Value }) => Value === tag.value);
      return {
        ...tag,
        children: topic && topic.Text,
      };
    }
    return tag;
  });
};

const ForumSearchFilters = props => {
  const { form, collapsed, setCollapsed, handleSearch, searching } = props;
  const [fieldOptions, setOptions] = useState({});
  const [tags, setTags] = useState([]);
  const router = useRouter();
  const { query } = router;

  useEffect(() => {
    (async () => {
      const options = await getForumFieldOptions();
      setOptions(options);
    })();
  }, []);

  useEffect(() => {
    const searchOptions = {};
    const formValues = {};
    const tagsValue = [];
    let resetForm = false;
    queryFields.forEach(({ key, option, field, formatter }) => {
      const val = query[key];
      if (val) {
        const formatVal = formatter ? formatter(val) : val;
        searchOptions[option] = formatVal;
        if (field) {
          formValues[option] = formatVal;
          resetForm = true;
          if (option === FIELD_NAMES.SEARCHTEXT) {
            tagsValue.push({
              children: formatVal,
              value: formatVal,
              input: option,
            });
          } else {
            tagsValue.push(
              ...formatVal.map(v => ({
                input: option,
                value: v,
              }))
            );
          }
        }
      }
    });
    if (resetForm) {
      form.setFieldsValue(formValues);
    }

    handleSearch(searchOptions);
    setTags(tagsValue);
  }, [query]);

  const states = fieldOptions.States || [];
  const topics = fieldOptions.Topics || [];

  const handleCollapseChange = keys => {
    setCollapsed(!keys.includes(FILTER_KEY));
  };

  const handleSubmit = e => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (err) {
        return;
      }

      const url = formatURL(router.asPath, {
        searchText: values.SearchText,
        states: values.States.join(','),
        topics: values.Topics.join(','),
      });

      let tagNames = 'All';
      if (values?.Topics?.length > 0 && topics?.length > 0) {
        tagNames = topics
          .filter(t => values.Topics.includes(t.Value))
          .map(t => escapeGTMParam(t.Text))
          .join(', ');
      }

      trackGTMEvent({
        event: 'marketForumSearch',
        searchText: escapeGTMParam(values?.SearchText || ''),
        state: values.States?.length > 0 ? values.States.join(', ') : 'All',
        tags: tagNames,
      });

      if (url !== router.asPath) {
        router.replace(url, url, { shallow: true });
      } else {
        handleSearch(values);
      }
    });
  };

  const handleTagClose = (e, value, input) => {
    e.preventDefault();
    const storedValue = form.getFieldValue(input);
    setTags(tags.filter(tag => tag.value !== value));
    if (input === FIELD_NAMES.SEARCHTEXT) {
      form.setFieldsValue({ [input]: '' });
    } else {
      const newVal = storedValue.filter(val => val !== value);
      form.setFieldsValue({ [input]: newVal });
    }
  };

  const handleValueChange = (value, input) => {
    const newTags = tags.filter(tag => tag.input !== input);
    if (input === FIELD_NAMES.SEARCHTEXT) {
      setTags([...newTags, { children: value, input, value }]);
    } else {
      const inputTags = value.map(v => ({ input, value: v }));
      setTags([...newTags, ...inputTags]);
    }
  };

  const searchTags = formatTags(tags, states, topics);

  return (
    <Form colon={false} onSubmit={handleSubmit}>
      <Row gutter={{ xs: 24, md: 32 }} type="flex">
        <Col xs={24} md={24} lg={collapsed ? 8 : 24}>
          <Button link href="/compliance/market-conduct/forum/new-thread" size="full">
            <Icon type="plus-circle" /> Start a New Thread
          </Button>
          <div css={style.filters}>
            <Collapse
              {...filterCollapseProps}
              activeKey={collapsed ? [] : [FILTER_KEY]}
              onChange={handleCollapseChange}
              css={style.collapse}
            >
              <Panel
                key={FILTER_KEY}
                header={
                  <Heading
                    type={3}
                    weight="bolder"
                    theme="primaryGray"
                    overrideStyles={style.title}
                  >
                    Filters
                  </Heading>
                }
                css={style.filterWrapper}
              >
                <Form.Item label="Search the Forum" css={m.b0}>
                  {form.getFieldDecorator('SearchText', {
                    initialValue: '',
                  })(
                    <SearchInput
                      disabled={searching}
                      onChange={e => handleValueChange(e.target.value, FIELD_NAMES.SEARCHTEXT)}
                    />
                  )}
                </Form.Item>
                <FilterSection label="State" css={style.collapse}>
                  <Form.Item css={m.b0}>
                    {form.getFieldDecorator('States', {
                      initialValue: [],
                    })(
                      <Select
                        maxTagCount={5}
                        maxTagTextLength={16}
                        css={formStyle.inputGray}
                        mode="multiple"
                        size="large"
                        optionFilterProp="children"
                        placeholder="Select State"
                        onChange={v => handleValueChange(v, FIELD_NAMES.STATES)}
                      >
                        {states.map(s => {
                          return (
                            <Select.Option key={s.Value} value={s.Value}>
                              {s.Text}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    )}
                  </Form.Item>
                </FilterSection>
                <FilterSection label="Tags" css={style.collapse}>
                  <Form.Item css={m.b0}>
                    {form.getFieldDecorator('Topics', {
                      initialValue: [],
                    })(
                      <Select
                        maxTagCount={5}
                        maxTagTextLength={16}
                        css={formStyle.inputGray}
                        mode="multiple"
                        size="large"
                        optionFilterProp="children"
                        placeholder="Select Tags"
                        onChange={v => handleValueChange(v, FIELD_NAMES.TOPICS)}
                      >
                        {topics.map(s => {
                          return (
                            <Select.Option key={s.Value} value={s.Value}>
                              {s.Text}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    )}
                  </Form.Item>
                </FilterSection>
                <div css={style.buttonContainer}>
                  <Button size="small" htmlType="submit" loading={searching}>
                    Apply
                  </Button>
                </div>
              </Panel>
            </Collapse>
          </div>
        </Col>
        <Col
          xs={collapsed ? 16 : 24}
          aria-hidden={!collapsed}
          css={!collapsed && style.filterTagsHidden}
        >
          <div css={style.filterTags}>
            {searchTags.map(tag => (
              <SearchTag key={tag.value} tag={tag} tagClose={handleTagClose} />
            ))}
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default Form.create('ForumSearchForm')(ForumSearchFilters);
